import { AvatarWithoutDot } from '@components/Avatar/Avatar'
import { Box, Divider, IconButton, Stack } from '@ui'
import { Paragraph } from 'ui/components/Text/Paragraph'
import { useGetRootKeyFromLinkedWallet, useUserLookup } from 'use-towns-client'
import { getPrettyDisplayName } from 'utils/getPrettyDisplayName'
import { isAddress } from 'viem'

type SocialLink = {
    type: 'x' | 'instagram' | 'website' | 'other'
    label?: string
    url: string
}

type Props = {
    aboutCreator: string | undefined
    owner: string | undefined
    creatorDisplayName: string | undefined
    socialLinks: SocialLink[] | undefined
}

const getSocialIcon = (type: SocialLink['type']) => {
    switch (type) {
        case 'x':
            return 'twitter'
        case 'instagram':
            return 'instagram'
        case 'website':
        case 'other':
        default:
            return 'link'
    }
}

const formatSocialUrl = (link: SocialLink): string => {
    if (link.type === 'x') {
        const handle = link.url.startsWith('@') ? link.url.slice(1) : link.url
        return `https://x.com/${handle}`
    }
    if (link.type === 'instagram') {
        const handle = link.url.startsWith('@') ? link.url.slice(1) : link.url
        return `https://instagram.com/${handle}`
    }
    // For website and other, return the URL as-is (should already be a full URL)
    if (link.url.startsWith('http://') || link.url.startsWith('https://')) {
        return link.url
    }
    return `https://${link.url}`
}

export const TownCreator = ({ aboutCreator, owner, creatorDisplayName, socialLinks }: Props) => {
    const validOwner = isAddress(owner ?? '') ? owner : undefined
    const { data: userId } = useGetRootKeyFromLinkedWallet({ walletAddress: validOwner })
    const user = useUserLookup(userId ?? '')

    if (
        !aboutCreator &&
        !userId &&
        !creatorDisplayName &&
        !(socialLinks && socialLinks.length > 0)
    ) {
        return null
    }

    const displayName = creatorDisplayName || getPrettyDisplayName(user)

    return (
        <>
            <Divider />
            <Box gap="lg">
                <Paragraph strong size="lg" color="default">
                    About the Creator
                </Paragraph>
                <Stack horizontal gap>
                    {userId && (
                        <Stack insetY="xxs">
                            <AvatarWithoutDot userId={userId} />
                        </Stack>
                    )}
                    <Box gap>
                        <Paragraph strong>{displayName}</Paragraph>
                        {aboutCreator ? (
                            <Paragraph color="gray2">{aboutCreator}</Paragraph>
                        ) : (
                            <Paragraph color="gray2">Towns Creator</Paragraph>
                        )}
                        {socialLinks && socialLinks.length > 0 && (
                            <Stack horizontal gap="sm">
                                {socialLinks.map((link, index) => (
                                    <IconButton
                                        as="a"
                                        key={`${link.type}`}
                                        href={formatSocialUrl(link)}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        icon={getSocialIcon(link.type)}
                                        size="square_sm"
                                        background="level2"
                                        color="default"
                                        padding="xxs"
                                    />
                                ))}
                            </Stack>
                        )}
                    </Box>
                </Stack>
            </Box>
        </>
    )
}
